/** Walks visible text nodes and converts metric/imperial suffixes per market config (snippets/measurement-system-data.liquid). */

import type { NumberFormatPattern } from 'utils/format-market-number';

import { formatInchAsFraction } from 'utils/format-inch-fraction';
import {
  decimalPlacesFromHint,
  formatMarketNumber,
  normalizeNumberFormatPattern,
  parseMarketNumber,
} from 'utils/format-market-number';

export type { NumberFormatPattern };

export type MeasurementSystemConfig = {
  numberFormatPattern?: NumberFormatPattern;
  marketHandle?: string;
  useInchFractions?: boolean;
  width: string;
  length: string;
  depth: string;
  diameter: string;
  weight: string;
  volume: string;
  coverage: string;
};

export type LinearDimension = keyof Pick<
  MeasurementSystemConfig,
  'width' | 'length' | 'depth' | 'diameter'
>;

type UnitCategory = 'linear' | 'weight' | 'volume' | 'area';

type CanonicalUnit = 'cm' | 'inch' | 'kg' | 'lbs' | 'ml' | 'fl oz' | 'm2' | 'sqft';

const SKIP_TAGS = new Set([
  'SCRIPT',
  'STYLE',
  'NOSCRIPT',
  'TEXTAREA',
  'INPUT',
  'SELECT',
  'OPTION',
  'CODE',
  'PRE',
]);

const ORIGINAL_TEXT = new WeakMap<Text, string>();

/** No word boundary after cm/mm: fails for values like 302cm. */
const LINEAR_UNIT_TAIL = '(?=\\s|$|[^0-9a-zA-Z"²])';

const LINEAR_COMPOUND_RE = new RegExp(
  '(\\d+(?:[.,]\\d+)?)(?:\\s*(?:x|×|\\*|X)\\s*(\\d+(?:[.,]\\d+)?))(?:\\s*(?:x|×|\\*|X)\\s*(\\d+(?:[.,]\\d+)?))?\\s*(cm|mm|m(?!²|2\\b)|in(?:ch(?:es)?)?|")' +
    LINEAR_UNIT_TAIL,
  'gi',
);

const LINEAR_RANGE_RE = new RegExp(
  '(\\d+(?:[.,]\\d+)?)\\s*[-–—]\\s*(\\d+(?:[.,]\\d+)?)\\s*(cm|mm|m(?!²|2\\b)|in(?:ch(?:es)?)?|")' +
    LINEAR_UNIT_TAIL,
  'gi',
);

/** No word boundary after m²/ft²: JS word boundary fails between ² and end-of-string. */
const AREA_UNIT_TAIL = '(?=\\s|$|[^0-9a-zA-Z])';

const AREA_RE = new RegExp(
  `(\\d+(?:[.,]\\d+)?)\\s*(m²|m2|sq\\.?\\s*m\\.?|ft²|ft2|sq\\.?\\s*ft\\.?)${AREA_UNIT_TAIL}`,
  'gi',
);

/** Negative lookbehind for slash skips denominators in inch fractions (e.g. 7/8 in). */
const MEASUREMENT_RE =
  /(?<!\/)(\d+(?:[.,]\d+)?)\s*(cm|mm|m(?!²|2\b)|kg|g|lb|lbs|ml|l|fl\s*\.?\s*oz|in(?:ch(?:es)?)?|")\b/gi;

const CONFIG_SELECTOR = 'script[type="application/json"][data-measurement-system]';

/** Skips follower counts etc. inside shopify-block-{uid}__progress_steps_{suffix}. */
const PROGRESS_STEPS_BLOCK_ID_MARKER = 'progress_steps';

const CONFIG_FIELDS = [
  'width',
  'length',
  'depth',
  'diameter',
  'weight',
  'volume',
  'coverage',
] as const satisfies ReadonlyArray<keyof MeasurementSystemConfig>;

const fieldCategory = (field: keyof MeasurementSystemConfig): UnitCategory => {
  if (field === 'weight') return 'weight';
  if (field === 'volume') return 'volume';
  if (field === 'coverage') return 'area';
  return 'linear';
};

const hasMarketMeasurementScript = (): boolean => document.querySelector(CONFIG_SELECTOR) != null;

const parseConfig = (): MeasurementSystemConfig | null => {
  if (!hasMarketMeasurementScript()) return null;

  const node = document.querySelector<HTMLScriptElement>(CONFIG_SELECTOR);
  if (!node?.textContent?.trim()) return null;

  try {
    return JSON.parse(node.textContent) as MeasurementSystemConfig;
  } catch {
    return null;
  }
};

const normalizeToken = (value: string): string =>
  value.trim().toLowerCase().replace(/²/g, '2').replace(/\s+/g, ' ').replace(/\./g, '');

const parseAreaSourceUnit = (raw: string): CanonicalUnit | null => {
  const token = normalizeToken(raw);

  if (token === 'm2' || token === 'sq m' || token === 'sqm') return 'm2';
  if (token === 'ft2' || token === 'sq ft' || token === 'sqft') return 'sqft';

  return null;
};

const parseSourceUnit = (raw: string): { category: UnitCategory; unit: CanonicalUnit } | null => {
  const token = normalizeToken(raw);

  if (token === 'cm') return { category: 'linear', unit: 'cm' };
  if (token === 'mm') return { category: 'linear', unit: 'cm' };
  if (token === 'm') return { category: 'linear', unit: 'cm' };
  if (token === 'in' || token === 'inch' || token === 'inches' || token === '"')
    return { category: 'linear', unit: 'inch' };

  if (token === 'kg') return { category: 'weight', unit: 'kg' };
  if (token === 'g') return { category: 'weight', unit: 'kg' };
  if (token === 'lb' || token === 'lbs') return { category: 'weight', unit: 'lbs' };

  if (token === 'ml') return { category: 'volume', unit: 'ml' };
  if (token === 'l') return { category: 'volume', unit: 'ml' };
  if (token === 'fl oz' || token === 'floz') return { category: 'volume', unit: 'fl oz' };

  return null;
};

/** Market `measurement_system` values: linear cm|inches, weight kg|lbs, volume ml|fl oz, coverage m2|ft2. */
export const marketLabel = (raw: string | null | undefined): string => String(raw ?? '').trim();

export const getMeasurementSystemConfig = (): MeasurementSystemConfig | null => parseConfig();

const useInchFractionsForMarket = (
  config: MeasurementSystemConfig | null = getMeasurementSystemConfig(),
): boolean => {
  if (!config) {
    return false;
  }

  if (config.useInchFractions === true) {
    return true;
  }

  return config.marketHandle?.trim().toLowerCase() === 'us';
};

export const linearUnitDisplaySuffix = (
  config: MeasurementSystemConfig | null,
  target: CanonicalUnit,
  category: UnitCategory,
): string => {
  if (category === 'linear' && target === 'inch' && useInchFractionsForMarket(config)) {
    return '"';
  }

  return '';
};

export const getMarketNumberFormatPattern = (): NumberFormatPattern =>
  normalizeNumberFormatPattern(getMeasurementSystemConfig()?.numberFormatPattern);

export const parseTargetUnit = (
  raw: string | null | undefined,
  category: UnitCategory,
): CanonicalUnit | null => {
  const token = normalizeToken(marketLabel(raw));
  if (!token) return null;

  if (category === 'linear') {
    if (token === 'cm') return 'cm';
    if (token === 'inches' || token === 'inch' || token === 'in') return 'inch';
    return null;
  }

  if (category === 'weight') {
    if (token === 'kg') return 'kg';
    if (token === 'lbs' || token === 'lb') return 'lbs';
    return null;
  }

  if (category === 'area') {
    if (token === 'm2') return 'm2';
    if (token === 'ft2') return 'sqft';
    return null;
  }

  if (token === 'ml') return 'ml';
  if (token === 'fl oz' || token === 'floz') return 'fl oz';

  return null;
};

const isMarketMeasurementConfigured = (config: MeasurementSystemConfig): boolean =>
  CONFIG_FIELDS.some((field) => parseTargetUnit(config[field], fieldCategory(field)) != null);

const widthLengthTarget = (config: MeasurementSystemConfig): CanonicalUnit | null => {
  const width = parseTargetUnit(config.width, 'linear');
  const length = parseTargetUnit(config.length, 'linear');
  if (!width || !length || width !== length) return null;
  return width;
};

const areaTarget = (config: MeasurementSystemConfig): CanonicalUnit | null =>
  parseTargetUnit(config.coverage, 'area');

const targetForCategory = (
  config: MeasurementSystemConfig,
  category: UnitCategory,
  linearDimension?: LinearDimension,
): CanonicalUnit | null => {
  if (category === 'linear') {
    if (linearDimension) {
      return parseTargetUnit(config[linearDimension], 'linear');
    }

    return (
      parseTargetUnit(config.width, 'linear') ??
      parseTargetUnit(config.length, 'linear') ??
      parseTargetUnit(config.depth, 'linear') ??
      parseTargetUnit(config.diameter, 'linear')
    );
  }

  if (category === 'weight') return parseTargetUnit(config.weight, 'weight');
  if (category === 'area') return areaTarget(config);
  return parseTargetUnit(config.volume, 'volume');
};

const parseNumber = (raw: string): number | null =>
  parseMarketNumber(raw, getMarketNumberFormatPattern());

export const formatNumber = (value: number, sourceRaw: string): string => {
  const pattern = getMarketNumberFormatPattern();

  return formatMarketNumber(value, {
    pattern,
    sourceHint: sourceRaw,
    decimalPlaces: Math.max(decimalPlacesFromHint(sourceRaw, pattern), 0),
  });
};

export const formatDisplayNumber = (
  value: number,
  sourceRaw: string,
  displayUnit?: CanonicalUnit,
  config: MeasurementSystemConfig | null = getMeasurementSystemConfig(),
): string => {
  if (displayUnit === 'inch' && useInchFractionsForMarket(config)) {
    return formatInchAsFraction(value);
  }

  return formatNumber(value, sourceRaw);
};

export const convertValue = (
  value: number,
  source: CanonicalUnit,
  target: CanonicalUnit,
): number | null => {
  if (source === target) return value;

  if (source === 'cm' && target === 'inch') return value / 2.54;
  if (source === 'inch' && target === 'cm') return value * 2.54;

  if (source === 'kg' && target === 'lbs') return value * 2.2046226218;
  if (source === 'lbs' && target === 'kg') return value / 2.2046226218;

  if (source === 'ml' && target === 'fl oz') return value / 29.5735295625;
  if (source === 'fl oz' && target === 'ml') return value * 29.5735295625;

  if (source === 'm2' && target === 'sqft') return value * 10.76391041671;
  if (source === 'sqft' && target === 'm2') return value / 10.76391041671;

  return null;
};

const valueInBase = (value: number, source: CanonicalUnit, rawUnit: string): number => {
  const token = normalizeToken(rawUnit);
  if (token === 'mm') return value / 10;
  if (token === 'm') return value * 100;
  if (token === 'g') return value / 1000;
  if (token === 'l') return value * 1000;
  return value;
};

const AREA_UNIT_IN_TEXT = new RegExp(
  `(\\d+(?:[.,]\\d+)?)(\\s*)(m|ft)(?:2|²)${AREA_UNIT_TAIL}`,
  'gi',
);

const createAreaUnitElement = (prefix: 'm' | 'ft'): HTMLSpanElement => {
  const span = document.createElement('span');
  span.className = 'measurement-area-unit';
  span.setAttribute('data-measurement-area-unit', '');
  span.append(document.createTextNode(prefix));
  const sup = document.createElement('sup');
  sup.className = 'measurement-area-unit__exp';
  sup.textContent = '2';
  span.append(sup);
  return span;
};

const textHasAreaUnit = (text: string): boolean => {
  AREA_UNIT_IN_TEXT.lastIndex = 0;
  return AREA_UNIT_IN_TEXT.test(text);
};

const hydrateAreaUnitsInTextNode = (textNode: Text, text: string): void => {
  const parent = textNode.parentNode;
  if (!parent) return;

  AREA_UNIT_IN_TEXT.lastIndex = 0;
  const fragment = document.createDocumentFragment();
  let lastIndex = 0;
  let match: RegExpExecArray | null = AREA_UNIT_IN_TEXT.exec(text);

  while (match) {
    if (match.index > lastIndex) {
      fragment.append(text.slice(lastIndex, match.index));
    }

    fragment.append(match[1] ?? '');
    if (match[2]) {
      fragment.append(match[2]);
    }
    fragment.append(createAreaUnitElement((match[3] ?? 'm').toLowerCase() as 'm' | 'ft'));
    lastIndex = match.index + match[0].length;
    match = AREA_UNIT_IN_TEXT.exec(text);
  }

  if (lastIndex < text.length) {
    fragment.append(text.slice(lastIndex));
  }

  parent.replaceChild(fragment, textNode);
};

const formatUnitFromConfig = (
  config: MeasurementSystemConfig,
  target: CanonicalUnit,
  category: UnitCategory,
): string => {
  const inchQuote = linearUnitDisplaySuffix(config, target, category);
  if (inchQuote) {
    return inchQuote;
  }

  const fields: Array<keyof MeasurementSystemConfig> =
    category === 'linear'
      ? ['width', 'length', 'depth', 'diameter']
      : category === 'weight'
        ? ['weight']
        : category === 'area'
          ? ['coverage']
          : ['volume'];

  const unitCategory = category;

  for (const field of fields) {
    const raw = config[field];
    if (typeof raw === 'string' && raw && parseTargetUnit(raw, unitCategory) === target) {
      return ` ${marketLabel(raw)}`;
    }
  }

  return ` ${target}`;
};

const convertLinearMeasurementParts = (
  config: MeasurementSystemConfig,
  unitRaw: string,
  parts: string[],
  match: string,
  joinSeparator: string,
): string => {
  const parsed = parseSourceUnit(unitRaw);
  const target =
    parsed?.category === 'linear'
      ? (widthLengthTarget(config) ?? targetForCategory(config, 'linear'))
      : parsed
        ? targetForCategory(config, parsed.category)
        : null;
  if (!parsed || !target || parsed.unit === target) return match;

  const converted = parts.map((part) => {
    const numeric = parseNumber(part);
    if (numeric == null) return part;

    const base = valueInBase(numeric, parsed.unit, unitRaw);
    const next = convertValue(base, parsed.unit, target);
    return next == null ? part : formatDisplayNumber(next, part, target, config);
  });

  const changed = converted.some((value, index) => {
    if (value !== parts[index]) {
      return true;
    }

    const part = parts[index];
    if (part == null) {
      return false;
    }

    const numeric = parseNumber(part);
    if (numeric == null) {
      return false;
    }

    const base = valueInBase(numeric, parsed.unit, unitRaw);
    const next = convertValue(base, parsed.unit, target);
    return next != null && Math.abs(next - base) > 1e-9;
  });

  if (!changed) {
    return match;
  }

  const joined = converted.join(joinSeparator);
  return `${joined}${formatUnitFromConfig(config, target, parsed.category)}`;
};

const rangeJoinSeparator = (match: string): string => {
  if (match.includes('–')) return ' – ';
  if (match.includes('—')) return ' — ';
  return ' - ';
};

const replaceCompound = (text: string, config: MeasurementSystemConfig): string => {
  LINEAR_COMPOUND_RE.lastIndex = 0;

  return text.replace(
    LINEAR_COMPOUND_RE,
    (match, first: string, second: string, third: string | undefined, unitRaw: string) => {
      const values = [first, second, third].filter((part): part is string => Boolean(part));
      const separator = match.includes('×') ? '×' : match.includes('X') ? 'X' : 'x';
      return convertLinearMeasurementParts(config, unitRaw, values, match, ` ${separator} `);
    },
  );
};

const replaceRange = (text: string, config: MeasurementSystemConfig): string => {
  LINEAR_RANGE_RE.lastIndex = 0;

  return text.replace(LINEAR_RANGE_RE, (match, first: string, second: string, unitRaw: string) =>
    convertLinearMeasurementParts(
      config,
      unitRaw,
      [first, second],
      match,
      rangeJoinSeparator(match),
    ),
  );
};

const replaceArea = (text: string, config: MeasurementSystemConfig): string => {
  AREA_RE.lastIndex = 0;

  return text.replace(AREA_RE, (match, numberRaw: string, unitRaw: string) => {
    const source = parseAreaSourceUnit(unitRaw);
    const target = targetForCategory(config, 'area');
    if (!source || !target || source === target) return match;

    const numeric = parseNumber(numberRaw);
    if (numeric == null) return match;

    const converted = convertValue(numeric, source, target);
    if (converted == null) return match;

    return `${formatNumber(converted, numberRaw)}${formatUnitFromConfig(config, target, 'area')}`;
  });
};

const replaceSingle = (text: string, config: MeasurementSystemConfig): string => {
  MEASUREMENT_RE.lastIndex = 0;

  return text.replace(MEASUREMENT_RE, (match, numberRaw: string, unitRaw: string) => {
    const parsed = parseSourceUnit(unitRaw);
    const target = parsed ? targetForCategory(config, parsed.category) : null;
    if (!parsed || !target || parsed.unit === target) return match;

    const numeric = parseNumber(numberRaw);
    if (numeric == null) return match;

    const base = valueInBase(numeric, parsed.unit, unitRaw);
    const converted = convertValue(base, parsed.unit, target);
    if (converted == null) return match;

    return `${formatDisplayNumber(converted, numberRaw, target, config)}${formatUnitFromConfig(config, target, parsed.category)}`;
  });
};

const convertText = (text: string, config: MeasurementSystemConfig): string => {
  const withCompounds = replaceCompound(text, config);
  const withRanges = replaceRange(withCompounds, config);
  const withArea = replaceArea(withRanges, config);
  return replaceSingle(withArea, config);
};

const shouldSkipNode = (node: Node): boolean => {
  let current: Node | null = node.parentNode;

  while (current) {
    if (current instanceof Element) {
      if (current.hasAttribute('data-measurement-skip')) return true;
      if (current.closest('[data-measurement-area-unit]')) return true;
      const elementId = current.getAttribute('id');
      if (elementId?.includes(PROGRESS_STEPS_BLOCK_ID_MARKER)) return true;
      if (current instanceof HTMLElement && SKIP_TAGS.has(current.tagName)) return true;
    }
    current = current.parentNode;
  }

  return false;
};

const processTextNode = (node: Text, config: MeasurementSystemConfig): void => {
  if (shouldSkipNode(node)) return;

  const current = node.textContent ?? '';
  if (!current.trim()) return;

  const original = ORIGINAL_TEXT.get(node) ?? current;
  if (!ORIGINAL_TEXT.has(node)) {
    ORIGINAL_TEXT.set(node, original);
  }

  const next = convertText(original, config);
  const canStyleArea = parseTargetUnit(config.coverage, 'area') != null;

  if (canStyleArea && textHasAreaUnit(next)) {
    hydrateAreaUnitsInTextNode(node, next);
    return;
  }

  if (next !== node.textContent) {
    node.textContent = next;
  }
};

const collectTextNodes = (root: ParentNode): Text[] => {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = [];

  let current = walker.nextNode();
  while (current) {
    if (current instanceof Text) {
      nodes.push(current);
    }
    current = walker.nextNode();
  }

  return nodes;
};

let activeConfig: MeasurementSystemConfig | null = null;

const scanMeasurementText = (root: ParentNode = document.body): void => {
  if (!activeConfig) return;

  for (const node of collectTextNodes(root)) {
    processTextNode(node, activeConfig);
  }
};

export const initMeasurementConversion = (): void => {
  if (!hasMarketMeasurementScript()) return;

  activeConfig = parseConfig();
  if (!activeConfig || !isMarketMeasurementConfigured(activeConfig)) return;

  scanMeasurementText();

  let frame = 0;
  const scheduleScan = (root?: ParentNode) => {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => {
      scanMeasurementText(root ?? document.body);
    });
  };

  const observer = new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'characterData' && record.target instanceof Text) {
        ORIGINAL_TEXT.delete(record.target);
      }
    }
    scheduleScan();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
  });

  window.addEventListener('product:variant-change', () => {
    scheduleScan(document.querySelector('main') ?? document.body);
  });

  document.addEventListener('shopify:section:load', () => {
    scheduleScan();
  });
};
